import React, { useState, useEffect } from 'react';
import { ServicePackage, CreateServicePackageRequest } from '../../types/service';
import ServicePackageCard from './ServicePackageCard';
import ManagePackageModal from './ManagePackageModal';
import ServicePackageDetailsModal from './ServicePackageDetailsModal';
import Button from '../common/Button';
import { PlusIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { useAppSelector } from '../../hooks/redux';

interface ServicePackagesListProps {
  userId: string;
  isOwner: boolean;
  onBook?: (pkg: ServicePackage) => void;
  onUpdate?: () => void;
}

const ServicePackagesList: React.FC<ServicePackagesListProps> = ({ userId, isOwner, onBook, onUpdate }) => {
  const [packages, setPackages] = useState<ServicePackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState<ServicePackage | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [viewingPackage, setViewingPackage] = useState<ServicePackage | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const { token } = useAppSelector((state) => state.auth);

  const fetchPackages = async (pageNum = 1) => {
    try {
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);

      const endpoint = isOwner ? '/services/my' : `/services/user/${userId}`;
      
      const response = await api.get(`${endpoint}?page=${pageNum}&limit=20`);
      if (response.data.success) {
        const newPackages = response.data.data;
        if (pageNum === 1) {
          setPackages(newPackages);
        } else {
          setPackages(prev => [...prev, ...newPackages]);
        }
        setHasMore(newPackages.length === 20);
        setPage(pageNum);
      }
    } catch (error) {
      console.error('Failed to fetch packages:', error);
      toast.error('Failed to load packages');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchPackages(1);
  }, [userId, isOwner]);

  const handleCreateOrUpdate = async (data: CreateServicePackageRequest) => {
    try {
      setSubmitting(true);
      
      if (editingPackage) {
        await api.put(`/services/${editingPackage.id}`, data);
        toast.success('Package updated successfully');
      } else {
        await api.post('/services', data);
        toast.success('Package created successfully');
      }
      
      setIsModalOpen(false);
      setEditingPackage(undefined);
      fetchPackages(1);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Failed to save package:', error);
      toast.error('Failed to save package');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (pkg: ServicePackage) => {
    if (!window.confirm('Are you sure you want to delete this package?')) return;

    try {
      await api.delete(`/services/${pkg.id}`);
      toast.success('Package deleted successfully');
      fetchPackages(1);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Failed to delete package:', error);
      toast.error('Failed to delete package');
    }
  };

  const handleBook = (pkg: ServicePackage) => {
    if (onBook) {
      onBook(pkg);
    } else {
      toast.success(`Booking request for ${pkg.title} initiated!`);
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-64 bg-gray-100 dark:bg-[#0B0F17] rounded-xl animate-pulse border border-gray-100 dark:border-gray-800" />
        ))}
      </div>
    );
  }

  const categories = ['All', ...Array.from(new Set(packages.filter(p => typeof p.category === 'string' && p.category.trim() !== '').map(p => p.category)))];
  const filteredPackages = selectedCategory === 'All' 
    ? packages 
    : packages.filter(p => p.category === selectedCategory);

  return (
    <div>
      {/* Category Filter */}
      {packages.length > 0 && categories.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-6">
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === category
                  ? 'bg-black text-white dark:bg-white dark:text-black shadow-sm'
                  : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      )}

      {/* Create Package Card & List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
        {isOwner && (
          <div 
            onClick={() => {
              setEditingPackage(undefined);
              setIsModalOpen(true);
            }}
            className="group cursor-pointer bg-gray-50 dark:bg-gray-800/50 border-2 border-dashed border-gray-200 dark:border-gray-800 rounded-2xl overflow-hidden"
          >
            <div className="bg-white dark:bg-gray-800/50 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 p-3 md:p-6 flex flex-col items-center justify-center text-center hover:border-black dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-all cursor-pointer group h-auto min-h-[120px] md:h-full">
              <div className="w-8 h-8 md:w-12 md:h-12 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-2 md:mb-4 group-hover:bg-white dark:group-hover:bg-gray-700 group-hover:shadow-sm transition-all">
                <PlusIcon className="h-4 w-4 md:h-6 md:w-6 text-gray-400 dark:text-gray-500 group-hover:text-black dark:group-hover:text-white" />
              </div>
              <h3 className="text-xs md:text-lg font-bold text-gray-900 dark:text-white mb-0.5 md:mb-2">Create Package</h3>
              <p className="text-[10px] md:text-sm text-gray-500 dark:text-gray-400 max-w-[200px]">Offer a new package to clients</p>
            </div>
          </div>
        )}

        {filteredPackages.map((pkg) => (
          <ServicePackageCard
            key={pkg.id}
            packageData={pkg}
            isOwner={isOwner}
            onEdit={(p) => {
              setEditingPackage(p);
              setIsModalOpen(true);
            }}
            onDelete={handleDelete}
            onBook={handleBook}
            onClick={(p) => {
              setViewingPackage(p);
              setIsDetailsModalOpen(true);
            }}
          />
        ))}
      </div>
      
      {/* Load More Trigger / Spinner */}
      {(hasMore || loadingMore) && !loading && (
        <div 
            className="w-full flex justify-center py-8"
            ref={(node) => {
                if (node && hasMore && !loadingMore) {
                    const observer = new IntersectionObserver((entries) => {
                        if (entries[0].isIntersecting) {
                            fetchPackages(page + 1);
                        }
                    }, { rootMargin: '400px' });
                    observer.observe(node);
                    return () => observer.disconnect();
                }
            }}
        >
            {loadingMore && (
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-200 dark:border-gray-700 border-t-black dark:border-t-white"></div>
            )}
        </div>
      )}

      {filteredPackages.length === 0 && !isOwner && packages.length > 0 && (
        <div className="text-center py-12 bg-gray-50 dark:bg-[#0B0F17]/50 rounded-2xl border border-dashed border-gray-200 dark:border-gray-800 col-span-full mt-6">
          <p className="text-gray-500 dark:text-gray-400 mb-4">No packages found for this category.</p>
        </div>
      )}

      {packages.length === 0 && !isOwner && (
        <div className="text-center py-12 bg-gray-50 dark:bg-[#0B0F17]/50 rounded-2xl border border-dashed border-gray-200 dark:border-gray-800 col-span-full">
          <p className="text-gray-500 dark:text-gray-400 mb-4">No packages listed yet.</p>
        </div>
      )}

      <ManagePackageModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingPackage(undefined);
        }}
        onSubmit={handleCreateOrUpdate}
        initialData={editingPackage}
        loading={submitting}
      />

      <ServicePackageDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false);
          setViewingPackage(null);
        }}
        packageData={viewingPackage}
        isOwner={isOwner}
        onEdit={(p) => {
          setEditingPackage(p);
          setIsModalOpen(true);
        }}
        onDelete={handleDelete}
        onBook={handleBook}
      />
    </div>
  );
};

export default ServicePackagesList;
